import React, { useState, useRef, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import windowWrapper from '../window_wrapper';

const ESCAPE_KEY = 27;
const MOUSEOUT_BUFFER_MS = 600;

interface IProps {
  trigger?: string | JSX.Element;
  coreLinkTrigger?: JSX.Element;
  contents: React.ReactNode;

  // this is effectively a callback allowing a controlling parent component
  // to set this dropdown as the activeDropdownKey and give that value
  // to other dropdowns
  onOpen: Function;

  // this is an arbitrary unique key
  dropdownKey: string;

  // if this value exists and doesn't match `dropdownKey`, the dropdown
  // will close immediately
  activeDropdownKey: string;

  // When this is set, the contents will be hidden even when open for mobile devices
  hideDropdownOnMobile?: boolean;

  disableDropdown?: boolean;

  onClose?: Function;
}

/** A buffered dropdown that won't immediately disappear on mouseout. */
export default function HeaderDropdown(props: IProps) {
  const {
    trigger,
    coreLinkTrigger,
    contents,
    onOpen,
    dropdownKey,
    activeDropdownKey,
    hideDropdownOnMobile,
    disableDropdown,
    onClose,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const timeoutIdRef = useRef<number | undefined>(undefined);
  const previousActiveDropdownKeyRef = useRef<string>(activeDropdownKey);
  const handleOutsideTouchRef = useRef<((evt: TouchEvent) => void) | null>(null);
  const handleEscapeKeyRef = useRef<((evt: KeyboardEvent) => void) | null>(null);
  const onCloseRef = useRef(onClose);

  // Keep onCloseRef current so document listeners always invoke the latest onClose
  onCloseRef.current = onClose;

  const detachDocumentListeners = useCallback(function detachDocumentListeners() {
    if (handleOutsideTouchRef.current) {
      document.removeEventListener('touchstart', handleOutsideTouchRef.current);
      handleOutsideTouchRef.current = null;
    }
    if (handleEscapeKeyRef.current) {
      document.removeEventListener('keydown', handleEscapeKeyRef.current);
      handleEscapeKeyRef.current = null;
    }
  }, []);

  const closeDropdown = useCallback(function closeDropdown() {
    detachDocumentListeners();
    setIsOpen(false);
    if (onCloseRef.current) onCloseRef.current();
  }, [detachDocumentListeners]);

  function attachDocumentListeners() {
    function handleOutsideTouch(evt: TouchEvent) {
      if (rootRef.current?.contains(evt.target as Node)) return;
      closeDropdown();
    }
    function handleEscapeKey(evt: KeyboardEvent) {
      if (evt.keyCode === ESCAPE_KEY) closeDropdown();
    }
    detachDocumentListeners();
    handleOutsideTouchRef.current = handleOutsideTouch;
    handleEscapeKeyRef.current = handleEscapeKey;
    document.addEventListener('touchstart', handleOutsideTouch);
    document.addEventListener('keydown', handleEscapeKey);
  }

  function openDropdown() {
    if (disableDropdown) return;
    setIsOpen(true);
    attachDocumentListeners();
    onOpen(dropdownKey);
  }

  function shouldClose() {
    return !rootRef.current?.querySelector(':hover');
  }

  function bufferedClose() {
    windowWrapper.clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = windowWrapper.setTimeout(
      function attemptBufferedClose() {
        if (shouldClose()) closeDropdown();
      },
      MOUSEOUT_BUFFER_MS,
    );
  }

  function handleTriggerClick(e) {
    // possible for touch devices to keep the trigger focused,
    // so we check isOpen to make sure the dropdown is open
    // before they visit the link.
    // also, if they tap after scrolling (which doesn't remove focus),
    // we reopen the modal
    if (disableDropdown) return;

    e.preventDefault();
    if (!isOpen) openDropdown();
  }

  function handleBlur() {
    // when the trigger loses focus, we want the dropdown to stay open if the user is focused within the dropdown
    // if the focus is not within this component, the dropdown should close
    if (!rootRef.current?.querySelector(':focus')) {
      closeDropdown();
    }
  }

  useEffect(function detachDocumentListenersOnUnmount() {
    return detachDocumentListeners;
  }, [detachDocumentListeners]);

  useEffect(function closeOnActiveDropdownKeyTransition() {
    const previousActiveDropdownKey = previousActiveDropdownKeyRef.current;
    previousActiveDropdownKeyRef.current = activeDropdownKey;

    if (!activeDropdownKey) return;
    if (previousActiveDropdownKey === activeDropdownKey) return;
    if (dropdownKey === activeDropdownKey) return;

    closeDropdown();
  }, [activeDropdownKey, dropdownKey, closeDropdown]);

  useEffect(function clearPendingCloseTimeoutOnUnmount() {
    return function clearPendingCloseTimeout() {
      windowWrapper.clearTimeout(timeoutIdRef.current);
    };
  }, []);

  const triggerId = `header-dropdown-${dropdownKey}`;

  function renderTrigger() {
    if (coreLinkTrigger) {
      return React.cloneElement(coreLinkTrigger, {
        className: `header-dropdown__trigger ${coreLinkTrigger.props.className ?? ''}`,
        onFocus: openDropdown,
        onClick: handleTriggerClick,
        'aria-expanded': isOpen,
        id: triggerId,
      });
    }
    return (
      <button
        className="header-dropdown__trigger"
        onFocus={openDropdown}
        onClick={handleTriggerClick}
        type="button"
        disabled={disableDropdown}
        aria-expanded={isOpen}
        id={triggerId}
      >
        {trigger}
      </button>
    );
  }

  const classes = classNames(
    'header-dropdown',
    { 'header-dropdown--open': isOpen },
  );

  const contentsClasses = classNames(
    'header-dropdown__contents',
    { 'header-dropdown__contents--mobile-hide': hideDropdownOnMobile },
  );

  return (
    <div
      ref={rootRef}
      className={classes}
      onMouseOver={openDropdown}
      onFocus={openDropdown}
      onMouseOut={bufferedClose}
      onBlur={handleBlur}
    >
      {renderTrigger()}
      <div
        className={contentsClasses}
        role="region"
        aria-labelledby={triggerId}
        hidden={!isOpen}
      >
        {contents}
      </div>
    </div>
  );
}
